import { TitleSchema, MissingTitleSchema } from '@gronebach-mdb/shared'
import type { Title, MissingTitle } from '@gronebach-mdb/shared'

export type RequestResult = 'success' | 'error' | 'rate_limited'

export const createRequested = async (data: {
  readonly title: string
  readonly type: 'movie' | 'series'
  readonly notes: string | null
  readonly honeypot: string
}): Promise<RequestResult> => {
  try {
    const response = await fetch('/api/requested', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: data.title,
        type: data.type,
        notes: data.notes,
        fulfilled: false,
        honeypot: data.honeypot,
      }),
    })
    if (response.status === 429) return 'rate_limited'
    if (!response.ok) return 'error'
    return 'success'
  } catch {
    return 'error'
  }
}

export type GetMoviesResult =
  | { ok: true; data: Array<Title> }
  | { ok: false; reason: 'validation_error' | 'unknown_error' }

export type ApiClient = {
  getMovies: () => Promise<GetMoviesResult>
}

export const createApiClient = (): ApiClient => ({
  getMovies: async () => {
    try {
      const response = await fetch('/api/titles')
      if (!response.ok) return { ok: false, reason: 'unknown_error' }
      const data: unknown = await response.json()
      const result = TitleSchema.array().safeParse(data)
      if (!result.success) {
        console.error('Validation failed:', result.error)
        return { ok: false, reason: 'validation_error' }
      }
      return { ok: true, data: result.data }
    } catch (err) {
      console.error(err)
      return { ok: false, reason: 'unknown_error' }
    }
  },
})

export type ReportResult = 'success' | 'error' | 'rate_limited'

export const reportTitle = async (imdbId: string, kind: 'film' | 'series', reason?: string): Promise<ReportResult> => {
  const route = kind === 'film' ? 'films' : 'series'
  try {
    const response = await fetch(`/api/${route}/${imdbId}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason ?? null }),
    })
    if (response.status === 429) return 'rate_limited'
    return response.ok ? 'success' : 'error'
  } catch {
    return 'error'
  }
}

export type GetMissingTitlesResult =
  | { ok: true; data: Array<MissingTitle> }
  | { ok: false; reason: 'validation_error' | 'unknown_error' }

export async function getMissingTitles(): Promise<GetMissingTitlesResult> {
  try {
    const response = await fetch('/api/missing')
    if (!response.ok) return { ok: false, reason: 'unknown_error' }
    const data: unknown = await response.json()
    const result = MissingTitleSchema.array().safeParse(data)
    if (!result.success) {
      console.error('getMissingTitles validation failed:', result.error)
      return { ok: false, reason: 'validation_error' }
    }
    return { ok: true, data: result.data }
  } catch (err) {
    console.error(err)
    return { ok: false, reason: 'unknown_error' }
  }
}

export function getMoviePosterUrl(poster: string | null): string | undefined {
  if (poster === null || poster === 'N/A') {
    return undefined
  }
  return poster
}
